import React from 'react'
import { SteamGame } from './types'

// Type guard to ensure we have valid SteamGame data
const isValidSteamGame = (game: unknown): game is SteamGame => {
  if (!game) return false

  const g = game as SteamGame
  return (
    typeof g.appId === 'number' &&
    g.appId > 0 &&
    typeof g.name === 'string' &&
    g.name.length > 0
  )
}

const buildSteamCoverUrl = (appId: number) =>
  `https://cdn.akamai.steamstatic.com/steam/apps/${appId}/header.jpg`

const getSteamImageSrc = (game: SteamGame) => {
  if (game.iconUrl?.startsWith('data:image')) {
    return game.iconUrl
  }
  return buildSteamCoverUrl(game.appId)
}

interface SteamGamesListProps {
  steamGames: SteamGame[]
  userLibraryGames: Map<number, string[]>
  selectedGames: Set<string>
  searchTerm: string
  visibleCount: number
  loadingGames: boolean
  setSearchTerm: (term: string) => void
  setSelectedGames: (games: Set<string>) => void
  setVisibleCount: (count: number | ((prev: number) => number)) => void
  toggleGameSelection: (gameId: string) => void
  onStartImport: (appIds?: number[]) => void
}

const SteamGamesList: React.FC<SteamGamesListProps> = ({
  steamGames,
  userLibraryGames,
  selectedGames,
  searchTerm,
  visibleCount,
  loadingGames,
  setSearchTerm,
  setSelectedGames,
  setVisibleCount,
  toggleGameSelection,
  onStartImport,
}) => {
  const [showOnlyNotInLibrary, setShowOnlyNotInLibrary] = React.useState(false)
  const [showOnlySelected, setShowOnlySelected] = React.useState(false)
  const [sortBy, setSortBy] = React.useState<'playtime' | 'name'>('playtime')

  const validGames = steamGames.filter(isValidSteamGame)
  const filteredGames = validGames
    .filter((game) =>
      game.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((game) => {
      if (showOnlyNotInLibrary) {
        return !userLibraryGames.get(game.appId)
      }
      return true
    })
    .filter((game) => {
      if (showOnlySelected) {
        return selectedGames.has(game.appId.toString())
      }
      return true
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name)
      }
      return b.playtime - a.playtime
    })

  const totalCount = validGames.length
  const inLibraryCount = validGames.filter((game) =>
    Boolean(userLibraryGames.get(game.appId))
  ).length
  const selectedCount = selectedGames.size

  const visibleGames = filteredGames.slice(0, visibleCount)
  const alphabet = React.useMemo(
    () => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
    []
  )
  const letterIndexMap = React.useMemo(() => {
    if (sortBy !== 'name') return new Map<string, number>()
    const map = new Map<string, number>()
    filteredGames.forEach((game, index) => {
      const firstChar = game.name.trim().charAt(0).toUpperCase()
      const letter = firstChar >= 'A' && firstChar <= 'Z' ? firstChar : '#'
      if (!map.has(letter)) {
        map.set(letter, index)
      }
    })
    return map
  }, [filteredGames, sortBy])

  const handleJumpToLetter = (letter: string) => {
    const index = letterIndexMap.get(letter)
    if (index === undefined) return
    const target = filteredGames[index]
    if (!target) return
    const element = document.getElementById(`steam-game-${target.appId}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const formatPlaytime = (minutes: number) =>
    minutes >= 60 ? `${(minutes / 60).toFixed(1)} hrs` : `${minutes} mins`

  const handleSelectAllVisible = () => {
    const filtered = visibleGames
      .filter((game) => !userLibraryGames.get(game.appId)?.includes('Steam'))
      .map((game) => game.appId.toString())
    setSelectedGames(new Set(filtered))
  }

  const handleDeselectAll = () => {
    setSelectedGames(new Set())
  }

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 30)
  }

  if (loadingGames) {
    return (
      <div className="flex justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    )
  }

  if (steamGames.length === 0) {
    return (
      <div className="alert alert-info">
        <span>No Steam games found in your library.</span>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold">Steam library</h3>
          <p className="text-sm text-base-content/60">
            Review and import your Steam games in the background.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="badge badge-outline">
            {totalCount} total
          </span>
          <span className="badge badge-outline">
            {inLibraryCount} in library
          </span>
          <span className="badge badge-outline">
            {selectedCount} selected
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-base-300 bg-base-100 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={() => onStartImport()}
            >
              Import all in background
            </button>
            <button
              className="btn btn-outline btn-sm"
              type="button"
              onClick={handleSelectAllVisible}
            >
              Select visible
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={handleDeselectAll}
            >
              Clear selection
            </button>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {selectedCount > 0 && (
              <button
                className="btn btn-outline btn-sm"
                type="button"
                onClick={() =>
                  onStartImport(Array.from(selectedGames).map((id) => Number(id)))
                }
              >
                Import {selectedCount} selected
              </button>
            )}
            <input
              type="text"
              className="input input-bordered input-sm w-full sm:w-56"
              placeholder="Search Steam games..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <div className="text-xs text-base-content/60">
            You can select games already in your library to add Steam as a
            platform. Games already marked as Steam are locked.
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={showOnlyNotInLibrary}
              onChange={(e) => setShowOnlyNotInLibrary(e.target.checked)}
            />
            <span>Not in library</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={showOnlySelected}
              onChange={(e) => setShowOnlySelected(e.target.checked)}
            />
            <span>Selected only</span>
          </label>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-base-content/60">Sort by</span>
            <select
              className="select select-bordered select-xs"
              value={sortBy}
              onChange={(e) =>
                setSortBy(e.target.value === 'name' ? 'name' : 'playtime')
              }
            >
              <option value="playtime">Playtime</option>
              <option value="name">Name</option>
            </select>
            {searchTerm.trim().length > 0 && (
              <button
                className="btn btn-outline btn-xs"
                type="button"
                onClick={() =>
                  onStartImport(filteredGames.map((game) => game.appId))
                }
              >
                Import {filteredGames.length} results
              </button>
            )}
          </div>
        </div>
        <div className="mt-2 text-xs text-base-content/60">
          Showing {visibleGames.length} of {filteredGames.length} results
          {searchTerm.trim().length > 0 && ` for "${searchTerm.trim()}"`}
        </div>
        {sortBy === 'name' && filteredGames.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1 text-xs">
            <button
              type="button"
              className={`btn btn-ghost btn-xs ${
                letterIndexMap.has('#') ? '' : 'btn-disabled'
              }`}
              onClick={() => handleJumpToLetter('#')}
            >
              #
            </button>
            {alphabet.map((letter) => (
              <button
                key={letter}
                type="button"
                className={`btn btn-ghost btn-xs ${
                  letterIndexMap.has(letter) ? '' : 'btn-disabled'
                }`}
                onClick={() => handleJumpToLetter(letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-3">
        {visibleGames.map((game) => {
          const platforms = userLibraryGames.get(game.appId)
          const hasSteam = platforms?.includes('Steam')
          const inLibrary = Boolean(platforms)
          const canAddSteam = !hasSteam

          return (
            <div
              key={game.appId}
              id={`steam-game-${game.appId}`}
              className={`flex flex-wrap items-center justify-between gap-4 rounded-lg border border-base-200 bg-base-100 p-3 transition ${
                hasSteam
                  ? 'opacity-60 cursor-not-allowed'
                  : 'cursor-pointer hover:border-primary/40 hover:shadow-sm'
              } ${
                selectedGames.has(game.appId.toString())
                  ? 'ring-2 ring-primary'
                  : inLibrary && canAddSteam
                  ? 'ring-1 ring-primary/40'
                  : ''
              }`}
              onClick={() => {
                if (canAddSteam) {
                  toggleGameSelection(game.appId.toString())
                }
              }}
            >
              <div className="flex items-center gap-3 min-w-[220px]">
                <img
                  src={getSteamImageSrc(game)}
                  alt={game.name}
                  className="h-12 w-12 rounded-md object-cover bg-base-200"
                  loading="lazy"
                  onError={(event) => {
                    const target = event.currentTarget
                    if (target.src !== game.iconUrl) {
                      target.src = game.iconUrl
                    }
                  }}
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{game.name}</h3>
                    {selectedGames.has(game.appId.toString()) && (
                      <span className="badge badge-primary badge-sm">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-base-content/60">
                    Playtime: {formatPlaytime(game.playtime)}
                  </p>
                  {inLibrary && !hasSteam && platforms && (
                    <p
                      className="text-[11px] text-base-content/60 truncate max-w-[240px]"
                      title={platforms.filter((p) => p !== 'Steam').join(', ')}
                    >
                      Owned on {platforms.filter((p) => p !== 'Steam').join(', ')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {hasSteam && (
                  <span className="badge badge-secondary badge-xs whitespace-nowrap">
                    Steam in library
                  </span>
                )}
                {inLibrary && !hasSteam && (
                  <span className="badge badge-outline badge-xs whitespace-nowrap">
                    In library
                  </span>
                )}
                {inLibrary && !hasSteam && (
                  <span className="badge badge-primary badge-xs whitespace-nowrap">
                    Add Steam
                  </span>
                )}
                {!inLibrary && (
                  <span className="badge badge-ghost badge-xs">New</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {visibleCount < filteredGames.length && (
        <div className="mt-4 flex justify-center">
          <button className="btn btn-outline" onClick={handleLoadMore}>
            Load More
          </button>
        </div>
      )}
      {selectedGames.size > 0 && (
        <div className="mt-4 flex justify-end">
          <button
            className="btn btn-primary"
            onClick={() =>
              onStartImport(Array.from(selectedGames).map((id) => Number(id)))
            }
          >
            Import {selectedGames.size} in background
          </button>
        </div>
      )}
    </>
  )
}

export default SteamGamesList
